import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { useConsumeScrollToCommentsOnMount } from "@/react/components/CommentsCompass/scrollToCommentsOnMount";

import {
  type ComposerFocusTarget,
  type ReplyFocusTarget,
  useComposerFocusRequest,
} from "./useComposerFocusRequest";

interface CommentsSectionContextValue {
  clearComposerFocusRequest: () => void;
  clearSectionScrollRequest: () => void;
  focusComposer: () => void;
  isCommentsOpen: boolean;
  isSectionScrollPending: boolean;
  isTopLevelFocusPending: boolean;
  matchReplyFocusTarget: (commentId: number) => ReplyFocusTarget | null;
  requestComposerFocus: (target: ComposerFocusTarget) => void;
  toggleComments: () => void;
}

const CommentsSectionContext =
  createContext<CommentsSectionContextValue | null>(null);

CommentsSectionContext.displayName = "CommentsSectionContext";

interface CommentsSectionProviderProps {
  children: ReactNode;
}

export const CommentsSectionProvider = ({
  children,
}: CommentsSectionProviderProps) => {
  const [isCommentsOpen, setIsCommentsOpen] = useState(true);
  const [isSectionScrollPending, setIsSectionScrollPending] = useState(false);
  const shouldScrollToSectionOnMount = useConsumeScrollToCommentsOnMount();
  const {
    requestComposerFocus,
    clearComposerFocusRequest,
    isTopLevelFocusPending,
    matchReplyFocusTarget,
  } = useComposerFocusRequest();

  const toggleComments = useCallback(() => {
    setIsCommentsOpen(prev => !prev);
  }, []);

  // Opens the section and focuses its primary composer. In hierarchy view the
  // top-level composer is hidden, so HierarchyView redirects this "section"
  // request onto the opened thread's reply composer.
  const focusComposer = useCallback(() => {
    setIsCommentsOpen(true);
    requestComposerFocus({ kind: "top-level" });
  }, [requestComposerFocus]);

  const clearSectionScrollRequest = useCallback(() => {
    setIsSectionScrollPending(false);
  }, []);

  // Arriving via a comment link / notification opens the section and scrolls to it.
  useEffect(() => {
    if (!shouldScrollToSectionOnMount) return;
    setIsCommentsOpen(true);
    setIsSectionScrollPending(true);
  }, [shouldScrollToSectionOnMount]);

  const value = useMemo(
    () => ({
      clearComposerFocusRequest,
      clearSectionScrollRequest,
      focusComposer,
      isCommentsOpen,
      isSectionScrollPending,
      isTopLevelFocusPending,
      matchReplyFocusTarget,
      requestComposerFocus,
      toggleComments,
    }),
    [
      clearComposerFocusRequest,
      clearSectionScrollRequest,
      focusComposer,
      isCommentsOpen,
      isSectionScrollPending,
      isTopLevelFocusPending,
      matchReplyFocusTarget,
      requestComposerFocus,
      toggleComments,
    ],
  );

  return (
    <CommentsSectionContext.Provider value={value}>
      {children}
    </CommentsSectionContext.Provider>
  );
};

export const useCommentsSection = (): CommentsSectionContextValue => {
  const value = useContext(CommentsSectionContext);
  if (!value) {
    throw new Error(
      "Comments section state is unavailable — mount Comments.Provider around the " +
        "comments UI and any external controls (engagement bars, comment buttons) " +
        "that interact with it.",
    );
  }
  return value;
};
